import { Injectable, NgZone } from "@angular/core";
import { AngularFireAuth } from "@angular/fire/compat/auth";
import { User, UserAdditionalData } from "src/services/user/user.interface";
import { UserApiService } from "src/services/user/user-api.service";
import { BehaviorSubject, filter, firstValueFrom, Observable, skip, take, timeout } from "rxjs";
import { isPlatform } from "@ionic/angular";
import { SocialLogin } from "@capgo/capacitor-social-login";
import { UserAlreadyExistsError } from "../user/errors/user-already-exists.error";
import { Role } from "src/services/user/role.enum";
import { SUPER_ADMIN_EMAILS } from "src/shared/super-admin.const";
import * as Sentry from "@sentry/angular";
import firebase from "firebase/compat/app";
import "firebase/compat/auth";
import { GoogleAuthProvider, OAuthProvider } from "@angular/fire/auth";

export enum AuthenticationProvidersApiName {
  apple = "apple.com",
  emailPassword = "password",
  facebook = "facebook.com",
  google = "google.com",
}

export enum AuthenticationProvidersDisplayName {
  apple = "Sign in with Apple",
  emailPassword = "email and password",
  facebook = "Log in with Facebook",
  google = "Log in with Google",
}

@Injectable({
  providedIn: "root",
})
export class AuthService {
  private activeUser: User | undefined;
  private readonly activeUserObservable = new BehaviorSubject<User | undefined>(undefined);
  private readonly authInitialized = new BehaviorSubject<boolean>(false);
  private loginInProgress = false;
  private devicePushToken: string | undefined;
  private savedPushToken: { token: string; userId: string } | undefined;

  constructor(
    private readonly firebaseAuth: AngularFireAuth,
    private readonly ngZone: NgZone,
    private readonly userApiService: UserApiService,
  ) {
    this.firebaseAuth.user.subscribe((firebaseUser) => this.handleFirebaseAuthStateChange(firebaseUser));
    this.activeUserObservable
      .pipe(filter((user): user is User => user !== undefined))
      .subscribe((user) => void this.storeDevicePushToken(user));
  }

  public static async getErrorMessageToDisplay(error: any): Promise<string> {
    if (error instanceof UserAlreadyExistsError) {
      return "A user with this email already exists. If this is your first time logging in, check your inbox for instructions on how to create your password.";
    }

    if (error.code === "auth/wrong-password") {
      return "You have entered an incorrect password, please check it and try again.";
    } else if (error.code === "auth/account-exists-with-different-credential") {
      return "An account already exists with this email using a different sign-in method. Please try signing in with the method you used previously.";
    } else if (error.code === "auth/invalid-email") {
      return "You have entered an invalid email, please check it and try again.";
    } else if (error.code === "auth/user-not-found") {
      return "This email does not exist.";
    } else {
      return `An unexpected error occurred: ${error.message ?? error.localizedDescription ?? error}`;
    }
  }

  public loginWithEmailUser(email: string, password: string): Promise<firebase.auth.UserCredential> {
    const lowerCasedEmail = email.toLowerCase();
    return this.firebaseAuth.signInWithEmailAndPassword(lowerCasedEmail, password);
  }

  public async loginWithApple(onSuccess: () => void | Promise<void>): Promise<void> {
    let userCredentials: firebase.auth.UserCredential;

    try {
      this.loginInProgress = true;

      if (isPlatform("ios") && !isPlatform("mobileweb")) {
        userCredentials = await this.loginWithAppleIos();
      } else {
        userCredentials = await this.loginWithAppleWeb();
      }

      await this.onLoginSuccess(userCredentials, onSuccess);
    } catch (err) {
      if (!this.shouldIgnoreError(err)) {
        throw err;
      }
    } finally {
      this.loginInProgress = false;
    }
  }

  public async loginWithGoogle(onSuccess: () => void | Promise<void>): Promise<void> {
    let userCredentials: firebase.auth.UserCredential;

    try {
      this.loginInProgress = true;

      if ((isPlatform("ios") || isPlatform("android")) && !isPlatform("mobileweb")) {
        userCredentials = await this.loginWithGoogleIos();
      } else {
        userCredentials = await this.loginWithGoogleWeb();
      }

      await this.onLoginSuccess(userCredentials, onSuccess);
    } catch (err) {
      if (!this.shouldIgnoreError(err)) {
        throw err;
      }
    } finally {
      this.loginInProgress = false;
    }
  }

  public async registerByEmail(email: string, password: string): Promise<{ email: string | null; uid: string }> {
    const lowerCasedEmail = email.toLowerCase();
    const userExists = await this.userApiService.userWithEmailExists(lowerCasedEmail);

    if (userExists.existsInFireAuth) {
      return this.requireFirebaseUser(await this.firebaseAuth.signInWithEmailAndPassword(lowerCasedEmail, password));
    }

    return this.requireFirebaseUser(await this.firebaseAuth.createUserWithEmailAndPassword(lowerCasedEmail, password));
  }

  public async loginComplete(firebaseUid: string, onSuccess?: () => void | Promise<void>): Promise<void> {
    const user = await this.userApiService.getByExternalId(firebaseUid);

    Sentry.setUser({ id: user.id, email: user.email, username: `${user.firstName} ${user.lastName}` });

    this.ngZone.run(() => {
      this.activeUser = user;
      this.activeUserObservable.next(this.activeUser);
    });

    await firstValueFrom(this.activeUserObservable.pipe(skip(1), take(1), timeout(1000))).catch(() => {});

    if (onSuccess) {
      await onSuccess();
    }
  }

  public async getActiveUser(): Promise<User | undefined> {
    if (this.activeUser) {
      return this.activeUser;
    }

    try {
      return await firstValueFrom(this.activeUserObservable.pipe(timeout(5000)));
    } catch {
      return undefined;
    }
  }

  public async waitForAuthInit(): Promise<void> {
    try {
      await firstValueFrom(
        this.authInitialized.pipe(
          filter((initialized) => initialized),
          timeout(3000),
        ),
      );
    } catch {
      console.warn("Timeout waiting for auth initialization");
    }
  }

  public async isActiveUserAdmin(): Promise<boolean> {
    return this.userHasRole([Role.admin]);
  }

  public async isActiveUserDriver(): Promise<boolean> {
    return this.userHasRole([Role.driver]);
  }

  public async isActiveUserAdminOrDriver(): Promise<boolean> {
    return this.userHasRole([Role.admin, Role.driver]);
  }

  public async isActiveUserSuperAdmin(): Promise<boolean> {
    const user = await this.getActiveUser();
    if (!user) return false;
    const isAdmin = user.role === Role.admin;
    const isInSuperAdminList = SUPER_ADMIN_EMAILS.includes(user.email?.toLowerCase());
    return isAdmin && isInSuperAdminList;
  }

  public getActiveUserObservable(): Observable<User | undefined> {
    return this.activeUserObservable;
  }

  public async logout(onSuccess?: () => void | Promise<void>) {
    Sentry.setUser(null);
    await this.firebaseAuth.signOut();
    await onSuccess?.();
  }

  public async logoutSync(onSuccess?: () => void | Promise<void>): Promise<void> {
    await this.firebaseAuth.signOut();
    await onSuccess?.();
  }

  public resetPassword(email: string): Promise<void> {
    return this.firebaseAuth.sendPasswordResetEmail(email);
  }

  public async createUserIfNotExist(
    user: { email: string | null; uid: string },
    additionalData: Omit<UserAdditionalData, "providerId"> | undefined,
    throwOnUserAlreadyExists: boolean,
    onSuccess?: () => void | Promise<void>,
  ) {
    const userExists = user.email ? await this.userApiService.userWithEmailExists(user.email) : undefined;

    if (user.email && userExists?.exists && throwOnUserAlreadyExists) {
      throw new UserAlreadyExistsError(user.email);
    }

    if (!userExists?.exists) {
      await this.userApiService.signUp({
        firstName: additionalData?.firstName,
        lastName: additionalData?.lastName,
        phone: additionalData?.phoneNumber,
      });
    }

    await this.loginComplete(user.uid, onSuccess);
  }

  public async savePushToken(token: string): Promise<void> {
    this.devicePushToken = token;

    if (this.activeUser) {
      await this.storeDevicePushToken(this.activeUser);
    }
  }

  public extractAdditionalDataFromUser(user: firebase.auth.UserCredential): UserAdditionalData {
    const nameParts = user.user?.displayName?.split(" ");
    return {
      firstName: nameParts?.length === 2 ? nameParts[0] : user.user?.displayName,
      lastName: nameParts?.length === 2 ? nameParts[1] : user.user?.displayName,
      company: "",
      phoneNumber: user.user?.phoneNumber,
      providerId: user.user?.providerData?.[0]?.providerId,
    };
  }

  private async loginWithAppleWeb(): Promise<firebase.auth.UserCredential> {
    const provider = new OAuthProvider("apple.com");
    return this.firebaseAuth.signInWithPopup(provider);
  }

  private async loginWithGoogleWeb(): Promise<firebase.auth.UserCredential> {
    const provider = new GoogleAuthProvider();
    return this.firebaseAuth.signInWithPopup(provider);
  }

  private async loginWithAppleIos(): Promise<firebase.auth.UserCredential> {
    const rawNonce = this.generateNonce();
    const hashedNonce = await this.sha256(rawNonce);

    const response = await SocialLogin.login({
      provider: "apple",
      options: {
        scopes: ["email", "name"],
        nonce: hashedNonce,
      },
    });

    if (!response?.result?.idToken) {
      throw new Error("Apple login failed: No ID token received");
    }

    const provider = new firebase.auth.OAuthProvider("apple.com");
    const credentials = provider.credential({
      idToken: response.result.idToken,
      rawNonce: rawNonce,
    });
    return this.firebaseAuth.signInWithCredential(credentials);
  }

  private async loginWithGoogleIos(): Promise<firebase.auth.UserCredential> {
    const response = await SocialLogin.login({
      provider: "google",
      options: {
        scopes: ["email", "profile"],
      },
    });

    if (response.result.responseType === "offline") {
      throw new Error("Google login returned offline mode response. Please check initialization.");
    }

    const idToken = response.result.idToken;
    const accessToken = response.result.accessToken?.token;

    if (!idToken) {
      throw new Error("Google login failed: No ID token received");
    }

    const credentials = accessToken
      ? firebase.auth.GoogleAuthProvider.credential(idToken, accessToken)
      : firebase.auth.GoogleAuthProvider.credential(idToken);

    return this.firebaseAuth.signInWithCredential(credentials);
  }

  private async onLoginSuccess(userCredentials: firebase.auth.UserCredential, onSuccess: () => void | Promise<void>) {
    await this.createUserIfNotExist(
      this.requireFirebaseUser(userCredentials),
      this.extractAdditionalDataFromUser(userCredentials),
      false,
      onSuccess,
    );
  }

  private requireFirebaseUser(userCredentials: firebase.auth.UserCredential): firebase.User {
    if (!userCredentials.user) {
      throw new Error("Authentication succeeded but no user was returned.");
    }

    return userCredentials.user;
  }

  private shouldIgnoreError(error: any) {
    const webCancelledError =
      error &&
      error.message &&
      (error.message.includes("The popup has been closed") ||
        error.message.includes("due to another conflicting popup"));
    const iosAppleError = error.code === "1001";
    const iosFacebookError = error.errorCode === "4201";
    const iosGoogleError =
      (typeof error === "string" && error.includes("The user canceled the sign-in flow")) ||
      (error.message && error.message.includes("User cancelled")) ||
      (error.message && error.message.includes("User canceled")) ||
      error.code === "USER_CANCELLED";

    return webCancelledError || iosAppleError || iosFacebookError || iosGoogleError;
  }

  private async userHasRole(roles: Role[]): Promise<boolean> {
    const user = await this.getActiveUser();

    if (!user) {
      return false;
    }

    return roles.includes(user.role);
  }

  private async handleFirebaseAuthStateChange(firebaseUser: firebase.User | null): Promise<void> {
    if (firebaseUser) {
      await this.subscribeToUserDocument(firebaseUser);
    } else {
      this.clearActiveUserState();
    }
  }

  private async subscribeToUserDocument(firebaseUser: firebase.User): Promise<void> {
    try {
      const user = await this.userApiService.getByExternalId(firebaseUser.uid);
      this.handleUserDocumentSuccess(user);
    } catch (error) {
      await this.handleUserDocumentError(error);
    }
  }

  private handleUserDocumentSuccess(user: User): void {
    this.ngZone.run(() => {
      this.activeUser = user;
      this.activeUserObservable.next(this.activeUser);
      this.authInitialized.next(true);
    });
  }

  private async handleUserDocumentError(error: any): Promise<void> {
    if (error?.code === "permission-denied") {
      await this.handlePermissionDeniedError();
    } else {
      this.markAuthAsInitialized();
    }
  }

  private async handlePermissionDeniedError(): Promise<void> {
    if (this.loginInProgress) {
      this.markAuthAsInitialized();
    } else {
      await this.signOutStaleAuthState();
    }
  }

  private async signOutStaleAuthState(): Promise<void> {
    await this.firebaseAuth.signOut().catch(() => {});
    this.clearActiveUserState();
  }

  private clearActiveUserState(): void {
    this.ngZone.run(() => {
      this.activeUser = undefined;
      this.activeUserObservable.next(undefined);
      this.authInitialized.next(true);
    });
  }

  private markAuthAsInitialized(): void {
    this.ngZone.run(() => {
      this.authInitialized.next(true);
    });
  }

  private generateNonce(length: number = 32): string {
    const charset = "0123456789ABCDEFGHIJKLMNOPQRSTUVXYZabcdefghijklmnopqrstuvwxyz-._";
    let result = "";
    const randomValues = new Uint8Array(length);
    crypto.getRandomValues(randomValues);
    randomValues.forEach((value) => {
      result += charset[value % charset.length];
    });
    return result;
  }

  private async sha256(str: string): Promise<string> {
    const encoder = new TextEncoder();
    const data = encoder.encode(str);
    const hashBuffer = await crypto.subtle.digest("SHA-256", data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map((byte) => byte.toString(16).padStart(2, "0")).join("");
  }

  private async storeDevicePushToken(user: User): Promise<void> {
    const token = this.devicePushToken;
    const alreadySaved = this.savedPushToken?.userId === user.id && this.savedPushToken.token === token;
    if (!token || user.pushToken === token || alreadySaved) {
      return;
    }

    this.savedPushToken = { token, userId: user.id };
    try {
      await this.userApiService.update(user.id, { pushToken: token });
    } catch (error) {
      this.savedPushToken = undefined;
      Sentry.captureException(error);
    }
  }
}
